'use client';

import React, { useState, useEffect } from 'react';
import Image from 'next/image';
import type { QuestionConfig, AnswerData, ValidationRules, Option } from '../types';
import { useTranslation } from '@/app/i18n/client';

interface CheckboxOption extends Option {
	imageUrl?: string;
	isNegativeOption?: boolean;
}

interface RankedAnswer {
	value: string;
	rank: number;
}

interface CheckboxQuestionProps {
	config: QuestionConfig;
	value: AnswerData;
	onChange: (value: AnswerData) => void;
	validationRules?: ValidationRules;
	error?: string;
	onEnterPress?: () => void; // Callback when Enter is pressed after selection
}

export default function CheckboxQuestion({ config, value, onChange, validationRules, error, onEnterPress }: CheckboxQuestionProps) {
	const { lang } = useTranslation();

	// Helper function to get text in correct language
	const getText = (th: string | undefined, en: string | undefined): string => {
		if (lang === 'en') {
			return en || th || '';
		}
		return th || en || '';
	};

	const options = (config.options || []) as CheckboxOption[];
	const maxSelections = config.maxSelections;
	const hasImages = options.some((opt) => !!opt.imageUrl);
	const layout = config.layout || 'horizontal';

	// Ranking feature
	const enableRanking = !!config.enableRanking;
	const rankingLimit = config.rankingLimit;

	// Limit (ranking uses rankingLimit, otherwise maxSelections)
	const effectiveLimit = enableRanking ? rankingLimit ?? maxSelections : maxSelections;

	// State for ranking mode
	const [rankedSelections, setRankedSelections] = useState<RankedAnswer[]>([]);

	// State for normal checkbox mode
	const selectedValues = !enableRanking ? ((value?.values || []) as string[]) : [];

	// Initialize ranked selections from saved value
	useEffect(() => {
		if (!enableRanking) return;
		if (!value?.values || !Array.isArray(value.values)) return;

		if (
			value.values.length > 0 &&
			typeof value.values[0] === 'object' &&
			value.values[0] !== null &&
			'rank' in (value.values[0] as Record<string, unknown>)
		) {
			setRankedSelections(value.values as RankedAnswer[]);
			return;
		}

		const ranked = (value.values as string[]).map((val, index) => ({
			value: val,
			rank: index + 1,
		}));
		setRankedSelections(ranked);
	}, [enableRanking, value?.values]);

	const getRank = (optionValue: string): number => {
		const item = rankedSelections.find((r) => r.value === optionValue);
		return item ? item.rank : 0;
	};

	const isRanked = (optionValue: string): boolean => {
		return rankedSelections.some((r) => r.value === optionValue);
	};

	// Check if a negative option is selected
	const hasNegativeOptionSelected = () => {
		if (enableRanking) {
			return rankedSelections.some((r) => {
				const option = options.find((opt) => opt.value === r.value);
				return option?.isNegativeOption;
			});
		}
		return selectedValues.some((val) => {
			const option = options.find((opt) => opt.value === val);
			return option?.isNegativeOption;
		});
	};

	// Check if any regular option is selected
	const hasRegularOptionSelected = () => {
		if (enableRanking) {
			return rankedSelections.some((r) => {
				const option = options.find((opt) => opt.value === r.value);
				return !option?.isNegativeOption;
			});
		}
		return selectedValues.some((val) => {
			const option = options.find((opt) => opt.value === val);
			return !option?.isNegativeOption;
		});
	};

	const isDisabled = (optionValue: string) => {
		const currentOption = options.find((opt) => opt.value === optionValue);
		const isCurrentSelected = enableRanking ? isRanked(optionValue) : selectedValues.includes(optionValue);
		
		// If this option is already selected, allow unselection
		if (isCurrentSelected) {
			return false;
		}
		
		// If this is a negative option and any other negative option is selected, disable it
		if (currentOption?.isNegativeOption && hasNegativeOptionSelected()) {
			return true;
		}
		
		// If this is a regular option and any negative option is selected, disable it
		if (!currentOption?.isNegativeOption && hasNegativeOptionSelected()) {
			return true;
		}

		if (effectiveLimit === undefined) return false;
		if (enableRanking) return rankedSelections.length >= effectiveLimit && !isRanked(optionValue);
		return selectedValues.length >= effectiveLimit && !selectedValues.includes(optionValue);
	};

	const handleRankingToggle = (optionValue: string) => {
		const existingIndex = rankedSelections.findIndex((item) => item.value === optionValue);

		if (existingIndex !== -1) {
			const newRanked = rankedSelections
				.filter((item) => item.value !== optionValue)
				.map((item, index) => ({
					value: item.value,
					rank: index + 1,
				}));

			setRankedSelections(newRanked);
			onChange({ values: newRanked.map((item) => item.value) });
			return;
		}

		if (effectiveLimit !== undefined && rankedSelections.length >= effectiveLimit) return;

		const newRanked = [...rankedSelections, { value: optionValue, rank: rankedSelections.length + 1 }];
		setRankedSelections(newRanked);
		onChange({ values: newRanked.map((item) => item.value) });
	};

	const handleToggle = (optionValue: string) => {
		if (enableRanking) {
			handleRankingToggle(optionValue);
			return;
		}

		let newValues: string[];
		const currentOption = options.find((opt) => opt.value === optionValue);
		const isCurrentNegative = currentOption?.isNegativeOption;

		if (selectedValues.includes(optionValue)) {
			// Uncheck the option
			newValues = selectedValues.filter((v) => v !== optionValue);
		} else {
			if (effectiveLimit !== undefined && selectedValues.length >= effectiveLimit) return;
			
			// When selecting a negative option, uncheck all other options
			if (isCurrentNegative) {
				newValues = [optionValue];
			}
			// When selecting a regular option, uncheck all negative options first
			else {
				const regularOnlyValues = selectedValues.filter((v) => {
					const opt = options.find((o) => o.value === v);
					return !opt?.isNegativeOption;
				});
				newValues = [...regularOnlyValues, optionValue];
			}
		}

		onChange({ values: newValues });
	};

	const currentCount = enableRanking ? rankedSelections.length : selectedValues.length;

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter' && currentCount > 0 && onEnterPress) {
			e.preventDefault();
			onEnterPress();
		}
	};

	return (
		<div className="flex flex-col items-center gap-4 w-full max-w-4xl mx-auto" onKeyDown={handleKeyDown} tabIndex={0}>
			{effectiveLimit !== undefined && (
				<div className="text-sm text-gray-600 text-center">
					{enableRanking
						? getText(
								`เลือก ${effectiveLimit} อันดับ (เลือกแล้ว ${currentCount}/${effectiveLimit})`,
								`Select ${effectiveLimit} ranks (Selected ${currentCount}/${effectiveLimit})`
							)
						: getText(
								`เลือกได้สูงสุด ${effectiveLimit} ข้อ (เลือกแล้ว ${currentCount}/${effectiveLimit})`,
								`Select up to ${effectiveLimit} items (Selected ${currentCount}/${effectiveLimit})`
							)}
				</div>
			)}

			<div
				className={`flex ${
					layout === 'vertical' ? 'flex-col w-full' : 'flex-row flex-wrap'
				} gap-2.5 justify-center w-full`}
			>
				{options.map((option) => {
					const isSelected = enableRanking ? isRanked(option.value) : selectedValues.includes(option.value);
					const disabled = isDisabled(option.value);
					const rank = enableRanking ? getRank(option.value) : 0;
					const optionLabel = getText(option.label, option.labelEn);

					// A full "pill" border-radius (100px) reads fine on a single-line
					// label, but on long text that wraps to several lines the same
					// radius still curves in at every corner, squeezing the first/last
					// lines against the border. For long labels, use a gentler radius
					// and extra horizontal padding instead so the text keeps clear space
					// from the edge regardless of how tall the button grows.
					const isLongLabel = !hasImages && optionLabel.length > 60;

					return (
						<button
							key={option.value}
							type="button"
							onClick={() => handleToggle(option.value)}
							disabled={disabled}
							className={`flex items-center gap-3 ${
								hasImages ? 'py-0 pr-2.5 pl-0' : isLongLabel ? 'py-4 px-7' : ' py-2.5 px-5'
							} ${isLongLabel ? 'rounded-2xl' : 'rounded-[100px]'} border-2 transition-all duration-200 ${
								layout === 'vertical'
									? `w-full ${enableRanking && rank > 0 ? 'justify-between' : 'justify-start'}`
									: hasImages
										? 'w-full sm:w-auto sm:min-w-[180px] max-w-full min-w-0 justify-start'
										: 'min-w-[120px] justify-center'
							} ${
								isSelected
									? 'border-[#028D7B] bg-[#028D7B] text-white shadow-md'
									: disabled
										? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
										: 'border-[#DDD] bg-white text-gray-700 hover:border-[#028D7B]/50 cursor-pointer'
							} ${error ? 'border-red-300' : ''}`}
						>
							<div className={`flex items-center ${hasImages ? 'gap-3 min-w-0' : 'gap-2'}`}>
								{hasImages && (
									<div className="relative w-16 h-16 rounded-full overflow-hidden bg-white shrink-0">
										{option.imageUrl ? (
											<img
												src={option.imageUrl}
												alt={optionLabel}
												className="object-cover w-full h-full"
												loading="eager"
											/>
										) : null}
									</div>
								)}
								<span className={`${layout === 'vertical' ? 'text-left' : ''} text-sm font-medium ${hasImages ? 'min-w-0 break-words text-left' : ''}`}>
									{optionLabel}
								</span>
							</div>

							{(enableRanking && rank > 0) && (<div className="flex items-center gap-2 shrink-0">
								{/* {isSelected && !enableRanking && (
									<svg
										className="w-4 h-4"
										fill="currentColor"
										viewBox="0 0 20 20"
										xmlns="http://www.w3.org/2000/svg"
									>
										<path
											fillRule="evenodd"
											d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
											clipRule="evenodd"
										/>
									</svg>
								)} */}
								
                                <div className="flex items-center justify-center min-w-[24px] h-6 px-2 rounded-full bg-white text-[#028D7B] text-sm font-bold shadow-sm">
                                    {rank}
                                </div>
								
							</div>)}
						</button>
					);
				})}
			</div>

			{error && <p className="text-red-600 text-sm mt-2 text-center">{error}</p>}
		</div>
	);
}
